import { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { motion, AnimatePresence, useMotionValue } from 'framer-motion';
import { Menu, X, PanelLeft } from 'lucide-react';

const STORAGE_KEY = 'floatingBtnPos';
const BTN_SIZE = 56;
const MARGIN = 16;
const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);
const brand = 'var(--theme-primary)';

export const FloatingNav = ({ items, activeTab, setActiveTab, onSwitchToSidebar }) => {
  const [open, setOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const [viewport, setViewport] = useState(() => ({
    w: typeof window !== 'undefined' ? window.innerWidth : 1280,
    h: typeof window !== 'undefined' ? window.innerHeight : 800,
  }));

  const getInitialPos = () => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const p = JSON.parse(raw);
        if (typeof p?.x === 'number' && typeof p?.y === 'number') {
          return {
            x: clamp(p.x, MARGIN, viewport.w - BTN_SIZE - MARGIN),
            y: clamp(p.y, MARGIN, viewport.h - BTN_SIZE - MARGIN),
          };
        }
      }
    } catch { /* ignore */ }
    return { x: viewport.w - BTN_SIZE - 24, y: viewport.h - BTN_SIZE - 24 };
  };

  const initial = useMemo(() => getInitialPos(), []);
  const x = useMotionValue(initial.x);
  const y = useMotionValue(initial.y);

  const dragConstraints = useMemo(() => ({
    left: MARGIN,
    top: MARGIN,
    right: viewport.w - BTN_SIZE - MARGIN,
    bottom: viewport.h - BTN_SIZE - MARGIN,
  }), [viewport]);

  useEffect(() => {
    const onResize = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      setViewport({ w, h });
      x.set(clamp(x.get(), MARGIN, w - BTN_SIZE - MARGIN));
      y.set(clamp(y.get(), MARGIN, h - BTN_SIZE - MARGIN));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [x, y]);

  // Flatten items without parent headers
  const allItems = useMemo(() => {
    const arr = [];
    items.forEach((item) => {
      if (Array.isArray(item.children) && item.children.length) {
        // Skip pushing the parent header item
        item.children.forEach((child) => {
          arr.push({ ...child, kind: 'nav-child' });
        });
      } else {
        arr.push({ ...item, kind: 'nav' });
      }
    });
    if (onSwitchToSidebar) {
      arr.push({ id: 'switch-sidebar', label: 'Sidebar', icon: PanelLeft, kind: 'sidebar' });
    }
    return arr;
  }, [items, onSwitchToSidebar]);

  const handleSelect = (id) => {
    setActiveTab(id);
    setOpen(false);
  };

  return (
    <>
      <motion.div
        drag
        dragMomentum={false}
        dragElastic={0}
        dragConstraints={dragConstraints}
        onDragStart={() => setIsDragging(true)}
        onDragEnd={() => {
          const next = {
            x: clamp(x.get(), dragConstraints.left, dragConstraints.right),
            y: clamp(y.get(), dragConstraints.top, dragConstraints.bottom),
          };
          x.set(next.x);
          y.set(next.y);
          try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
          setTimeout(() => setIsDragging(false), 0);
        }}
        style={{ position: 'fixed', top: 0, left: 0, x, y, zIndex: open ? 10002 : 9999, touchAction: 'none' }}
      >
        <button
          type="button"
          onClick={() => { if (!isDragging) setOpen((prev) => !prev); }}
          className="w-14 h-14 rounded-full flex items-center justify-center transition-transform active:scale-95 hover:scale-105"
          style={{ background: brand, color: '#fff', boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </motion.div>

      {/* App Drawer Overlay */}
      <AnimatePresence>
        {open && (
          <>
            {/* Standard Light Blur Backdrop */}
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="fixed inset-0"
              style={{ zIndex: 10000, background: 'rgba(28, 27, 27, 0.4)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' }}
              onClick={() => setOpen(false)}
            />

            {/* Floating Icons Grid */}
            <motion.div
              key="module"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 350 }}
              className="fixed inset-0 overflow-y-auto overscroll-contain pointer-events-none"
              style={{ zIndex: 10001 }}
            >
              {/* Centred while it fits, scrolled from the top once it does not.
                  `justify-center` on the scroll container itself cannot do that:
                  when the grid is taller than the window it centres the overflow
                  too, pushing the first row off the top edge where scrolling
                  cannot reach it. `min-h-full` on an inner flex wrapper keeps
                  the centring for short lists and hands the tall ones an honest
                  scroll. */}
              <div className="flex min-h-full items-center justify-center p-4 sm:p-6">
                <div
                  className="w-full max-w-5xl flex flex-col pointer-events-auto relative"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Rows breathe, but not so much that eight of them stop
                      fitting on a laptop. */}
                  <div className="flex-1 px-4 py-4">
                    <div className="grid grid-cols-3 min-[420px]:grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 gap-y-6 sm:gap-y-8 gap-x-2">
                    {allItems.map((item) => {
                      const Icon = item.icon;
                      const isSelectable = item.kind === 'nav' || item.kind === 'nav-child' || item.kind === 'sidebar';
                      const isParentLabel = item.kind === 'nav-parent';
                      const isActive = (isSelectable && item.id === activeTab);

                      if (isParentLabel) {
                        return (
                          <div key={item.id} className="col-span-full pt-6 pb-1 border-b border-slate-300/40">
                            <span className="text-xs font-extrabold text-slate-500 uppercase tracking-[0.2em]">{item.label}</span>
                          </div>
                        );
                      }

                      const onClick = () => {
                        if (isSelectable && item.kind !== 'sidebar') handleSelect(item.id);
                        else if (item.kind === 'sidebar') { setOpen(false); onSwitchToSidebar(); }
                      };

                      return (
                        <div key={item.id} className="flex flex-col items-center gap-3">
                          <button
                            type="button"
                            onClick={onClick}
                            aria-current={isActive ? 'page' : undefined}
                            className="w-16 h-16 sm:w-18 sm:h-18 rounded-[2.05rem] flex items-center justify-center transition-all active:scale-90 hover:scale-105"
                            style={{
                              background: isActive ? '#fff' : '#fff',
                              color: isActive ? brand : '#475569',
                              boxShadow: isActive ? '0 8px 24px rgba(0,0,0,0.15)' : '0 4px 12px rgba(0,0,0,0.05)',
                              border: isActive ? `4px solid ${brand}` : '1px solid #e2e8f0',
                            }}
                          >
                            <Icon size={26} aria-hidden="true" />
                          </button>
                          <span
                            className="text-[13px] font-semibold tracking-tight text-center leading-tight w-full px-1"
                            style={{ color: isActive ? brand : '#1e293b' }}
                          >
                            {item.label}
                          </span>
                        </div>
                      );
                    })}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

FloatingNav.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    icon: PropTypes.elementType.isRequired,
    children: PropTypes.array,
  })).isRequired,
  activeTab: PropTypes.string.isRequired,
  setActiveTab: PropTypes.func.isRequired,
  onSwitchToSidebar: PropTypes.func,
};
